import type { VideoJsPlayerOptions } from 'video.js';

import type { PreloadSources, VideoLayout } from '../player.config';
import type { Tweaks } from '../player.interface';

import { useMemo } from 'react';

import { supportsNativeHls } from '../utils/supports-native-hls';

import '../videojs-poster';

export function useVideoJsOptions(
  maxPlaylistRetries: number,
  looping: boolean,
  muted: boolean,
  aspectRatio?: '16:9' | '4:3',
  videoLayout?: VideoLayout,
  preloadSources?: PreloadSources,
  tweaks?: Tweaks
): VideoJsPlayerOptions {
  const disableNetworkInformationApi = tweaks?.disableNetworkInformationApi;

  const options: VideoJsPlayerOptions = useMemo(() => {
    const layout = videoLayout || 'fluid';
    return {
      children: ['mediaLoader', 'miPoster', 'liveTracker'],
      html5: {
        vhs: {
          maxPlaylistRetries,
          overrideNative: !supportsNativeHls(),
          useDevicePixelRatio: true, // Helps with getting better quality in high DPI displays
          useNetworkInformationApi: disableNetworkInformationApi === undefined ? true : !disableNetworkInformationApi,
        },
        nativeTextTracks: supportsNativeHls(),
        nativeAudioTracks: supportsNativeHls(),
      },
      loop: looping,
      muted,
      fluid: layout === 'fluid',
      fill: layout === 'fill',
      playsinline: true,
      disablePictureInPicture: true,
      aspectRatio: layout === 'fluid' ? aspectRatio : undefined,
      preload: preloadSources || 'auto',
    };
  }, [videoLayout, maxPlaylistRetries, looping, muted, aspectRatio, preloadSources, disableNetworkInformationApi]);

  return options;
}
